import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Mail, Users, Search, Plus, Trash2, X, Folder, FolderOpen, ChevronLeft, ChevronRight, UserMinus, UserPlus, Loader2, CheckSquare, Square } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  getRecipients,
  createRecipient,
  deleteRecipient,
  enrollRecipientInSequence,
  getEmailSequences,
  getRecipientGroups,
  deleteRecipientGroup,
  getRecipientGroupMembers,
  removeRecipientFromGroup,
  createRecipientGroup,
  addRecipientsToGroup
} from '../../services/api'

// Numbered Pagination Component
function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null

  const getVisiblePages = () => {
    const pages = []
    const maxVisible = 7
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      
      if (currentPage > 3) pages.push('...')
      
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i)
      }
      
      if (currentPage < totalPages - 2) pages.push('...')
      
      if (!pages.includes(totalPages)) pages.push(totalPages)
    }
    
    return pages
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', marginTop: '1rem' }}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={{
          padding: '0.4rem',
          background: currentPage === 1 ? 'transparent' : 'rgb(var(--rgb-ink) / 0.05)',
          border: '1px solid rgb(var(--rgb-ink) / 0.1)',
          borderRadius: '6px',
          color: currentPage === 1 ? 'var(--color-text-disabled)' : 'rgb(var(--rgb-ink) / 0.75)',
          cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
        }}
      >
        <ChevronLeft size={16} />
      </button>
      
      {getVisiblePages().map((page, idx) => (
        page === '...' ? (
          <span key={`ellipsis-${idx}`} style={{ padding: '0 0.5rem', color: 'var(--color-text-muted)' }}>...</span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            style={{
              padding: '0.4rem 0.7rem',
              background: page === currentPage ? 'rgb(var(--rgb-brand) / 0.15)' : 'rgb(var(--rgb-ink) / 0.05)',
              border: page === currentPage ? '1px solid rgb(var(--rgb-brand) / 0.4)' : '1px solid rgb(var(--rgb-ink) / 0.1)',
              borderRadius: '6px',
              color: page === currentPage ? 'rgb(var(--rgb-brand))' : 'rgb(var(--rgb-ink) / 0.75)',
              cursor: 'pointer',
              fontWeight: page === currentPage ? 600 : 400,
              fontSize: '0.85rem'
            }}
          >
            {page}
          </button>
        )
      ))}
      
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        style={{
          padding: '0.4rem',
          background: currentPage === totalPages ? 'transparent' : 'rgb(var(--rgb-ink) / 0.05)',
          border: '1px solid rgb(var(--rgb-ink) / 0.1)',
          borderRadius: '6px',
          color: currentPage === totalPages ? 'var(--color-text-disabled)' : 'rgb(var(--rgb-ink) / 0.75)',
          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
        }}
      >
        <ChevronRight size={16} />
      </button>
    </div>
  )
}

export default function RecipientsList() {
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [limit] = useState(50)
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '' })
  const [enrollRecipient, setEnrollRecipient] = useState(null)
  
  // Folder navigation state
  const [currentGroup, setCurrentGroup] = useState(null) // null = root (all recipients), object = inside a group
  const [showCreateGroup, setShowCreateGroup] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [newGroupColor, setNewGroupColor] = useState('rgb(var(--rgb-brand))')
  const [showAddMembersModal, setShowAddMembersModal] = useState(false)

  // Fetch all recipients (root level)
  const { data: recipientsData, isLoading: loadingRecipients } = useQuery({
    queryKey: ['recipients', searchQuery, page, limit],
    queryFn: () => getRecipients({ q: searchQuery || null, page, limit }),
    keepPreviousData: true,
    enabled: !currentGroup
  })

  // Fetch all groups
  const { data: groupsData, isLoading: loadingGroups } = useQuery({
    queryKey: ['recipientGroups'],
    queryFn: getRecipientGroups
  })

  // Fetch members of current group
  const { data: membersData, isLoading: loadingMembers } = useQuery({
    queryKey: ['recipientGroupMembers', currentGroup?.id, searchQuery, page, limit],
    queryFn: () => getRecipientGroupMembers(currentGroup.id, { q: searchQuery || null, page, limit }),
    keepPreviousData: true,
    enabled: !!currentGroup
  })

  const { data: sequencesData } = useQuery({
    queryKey: ['emailSequences'],
    queryFn: getEmailSequences
  })

  const sequences = sequencesData?.member || sequencesData?.['hydra:member'] || sequencesData || []
  const groups = groupsData?.member || groupsData?.['hydra:member'] || groupsData || []
  
  // Data based on current view
  const recipients = currentGroup 
    ? (membersData?.items || [])
    : (recipientsData?.items || [])
  const total = currentGroup
    ? (membersData?.total || 0)
    : (recipientsData?.total || 0)
  const totalPages = Math.ceil(total / limit)

  const isLoading = currentGroup ? loadingMembers : (loadingRecipients || loadingGroups)

  // Mutations
  const createMutation = useMutation({
    mutationFn: () => createRecipient({
      email: form.email.trim(),
      firstName: form.firstName.trim() || null,
      lastName: form.lastName.trim() || null
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipients'] })
      setForm({ email: '', firstName: '', lastName: '' })
      toast.success('Destinataire ajouté')
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || error?.message || 'Erreur lors de la création')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteRecipient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipients'] })
      queryClient.invalidateQueries({ queryKey: ['recipientGroupMembers'] })
      toast.success('Destinataire supprimé')
    },
    onError: () => toast.error('Erreur lors de la suppression')
  })

  const deleteGroupMutation = useMutation({
    mutationFn: (id) => deleteRecipientGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipientGroups'] })
      toast.success('Groupe supprimé')
    },
    onError: () => toast.error('Erreur lors de la suppression du groupe')
  })

  const removeFromGroupMutation = useMutation({
    mutationFn: ({ groupId, recipientId }) => removeRecipientFromGroup(groupId, recipientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipientGroupMembers'] })
      queryClient.invalidateQueries({ queryKey: ['recipientGroups'] })
      toast.success('Destinataire retiré du groupe')
    },
    onError: () => toast.error('Erreur lors du retrait')
  })

  const createGroupMutation = useMutation({
    mutationFn: () => createRecipientGroup({ name: newGroupName.trim(), color: newGroupColor }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipientGroups'] })
      setNewGroupName('')
      setShowCreateGroup(false)
      toast.success('Groupe créé')
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Erreur lors de la création du groupe')
    }
  })

  const handleCreate = (e) => {
    e.preventDefault()
    if (!form.email.trim()) {
      toast.error('Email requis')
      return
    }
    createMutation.mutate()
  }

  const handleCreateGroup = (e) => {
    e.preventDefault()
    if (!newGroupName.trim()) {
      toast.error('Nom du groupe requis')
      return
    }
    createGroupMutation.mutate()
  }

  const enterGroup = (group) => {
    setCurrentGroup(group)
    setPage(1)
    setSearchQuery('')
  }

  const exitGroup = () => {
    setCurrentGroup(null)
    setPage(1)
    setSearchQuery('')
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-secondary)' }}>
        <div className="spinning" style={{ width: 48, height: 48, border: '4px solid rgb(var(--rgb-brand) / 0.1)', borderTop: '4px solid rgb(var(--rgb-brand))', borderRadius: '50%', margin: '0 auto 1rem', animation: 'spin 1s linear infinite' }}></div>
        Chargement...
      </div>
    )
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '0.5rem', 
        marginBottom: '1rem',
        padding: '0.5rem 0',
        borderBottom: '1px solid rgb(var(--rgb-ink) / 0.1)'
      }}>
        <button
          onClick={exitGroup}
          style={{
            background: !currentGroup ? 'rgb(var(--rgb-brand) / 0.15)' : 'transparent',
            border: !currentGroup ? '1px solid rgb(var(--rgb-brand) / 0.3)' : '1px solid transparent',
            color: !currentGroup ? 'rgb(var(--rgb-brand))' : 'var(--color-text-secondary)',
            padding: '0.4rem 0.8rem',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            fontSize: '0.85rem'
          }}
        >
          <Users size={14} />
          Tous les destinataires
        </button>
        {currentGroup && (
          <>
            <ChevronRight size={14} style={{ color: 'var(--color-text-disabled)' }} />
            <span style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.4rem',
              padding: '0.4rem 0.8rem',
              background: 'rgb(var(--rgb-brand) / 0.15)',
              border: '1px solid rgb(var(--rgb-brand) / 0.3)',
              borderRadius: '6px',
              color: 'rgb(var(--rgb-brand))',
              fontSize: '0.85rem'
            }}>
              <FolderOpen size={14} style={{ color: currentGroup.color }} />
              {currentGroup.name}
            </span>
            <button
              onClick={() => setShowAddMembersModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.4rem 0.8rem',
                background: 'rgba(59,130,246,0.15)',
                border: '1px solid rgba(59,130,246,0.35)',
                borderRadius: '6px',
                color: '#3b82f6',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 500
              }}
            >
              <UserPlus size={14} />
              Ajouter des membres
            </button>
          </>
        )}
        <div style={{ marginLeft: 'auto', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
          {total} destinataire{total !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Add recipient form (only at root level or inside group) */}
      {!currentGroup && (
        <form
          onSubmit={handleCreate}
          style={{
            display: 'grid',
            gridTemplateColumns: '1.6fr 1fr 1fr auto',
            gap: '0.75rem',
            marginBottom: '1.5rem'
          }}
        >
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email"
            style={{
              background: 'rgb(var(--rgb-ink) / 0.05)',
              border: '1px solid rgb(var(--rgb-ink) / 0.1)',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              color: 'var(--color-text)'
            }}
          />
          <input
            type="text"
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            placeholder="Prénom"
            style={{
              background: 'rgb(var(--rgb-ink) / 0.05)',
              border: '1px solid rgb(var(--rgb-ink) / 0.1)',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              color: 'var(--color-text)'
            }}
          />
          <input
            type="text"
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            placeholder="Nom"
            style={{
              background: 'rgb(var(--rgb-ink) / 0.05)',
              border: '1px solid rgb(var(--rgb-ink) / 0.1)',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              color: 'var(--color-text)'
            }}
          />
          <button
            type="submit"
            disabled={createMutation.isLoading}
            style={{
              background: 'rgb(var(--rgb-brand) / 0.15)',
              border: '1px solid rgb(var(--rgb-brand) / 0.35)',
              color: 'rgb(var(--rgb-brand))',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            <Plus size={16} />
            Ajouter
          </button>
        </form>
      )}

      {/* Search */}
      <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
        <Search
          size={18}
          style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--color-text-secondary)'
          }}
        />
        <input
          type="text"
          placeholder={currentGroup ? `Rechercher dans ${currentGroup.name}...` : 'Rechercher un destinataire...'}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            setPage(1)
          }}
          style={{
            width: '100%',
            background: 'rgb(var(--rgb-ink) / 0.05)',
            border: '1px solid rgb(var(--rgb-ink) / 0.1)',
            borderRadius: '8px',
            padding: '0.75rem 1rem 0.75rem 3rem',
            color: 'var(--color-text)',
            fontSize: '0.875rem'
          }}
        />
      </div>

      {/* Groups Section (only at root level) */}
      {!currentGroup && groups.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '0.75rem' 
          }}>
            <h4 style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              <Folder size={14} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
              Groupes ({groups.length})
            </h4>
            <button
              onClick={() => setShowCreateGroup(true)}
              style={{
                background: 'transparent',
                border: '1px solid rgb(var(--rgb-ink) / 0.15)',
                color: 'var(--color-text-secondary)',
                padding: '0.3rem 0.6rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem'
              }}
            >
              <Plus size={12} />
              Nouveau groupe
            </button>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '0.75rem'
          }}>
            {groups.map((group) => (
              <div
                key={group.id}
                style={{
                  background: 'rgb(var(--rgb-ink) / 0.03)',
                  border: '1px solid rgb(var(--rgb-ink) / 0.08)',
                  borderRadius: '8px',
                  padding: '0.75rem 1rem',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderLeft: `3px solid ${group.color || 'rgb(var(--rgb-brand))'}`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.06)'
                  e.currentTarget.style.borderColor = 'rgb(var(--rgb-ink) / 0.15)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.03)'
                  e.currentTarget.style.borderColor = 'rgb(var(--rgb-ink) / 0.08)'
                }}
              >
                <div 
                  onClick={() => enterGroup(group)}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Folder size={18} style={{ color: group.color || 'rgb(var(--rgb-brand))' }} />
                  <div>
                    <div style={{ fontWeight: 500, color: 'var(--color-text)', fontSize: '0.9rem' }}>{group.name}</div>
                    <div style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                      {group.recipientCount ?? group.recipients?.length ?? 0} membre{(group.recipientCount ?? group.recipients?.length ?? 0) !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (confirm(`Supprimer le groupe "${group.name}" ?`)) {
                      deleteGroupMutation.mutate(group.id)
                    }
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--color-text-muted)',
                    cursor: 'pointer',
                    padding: '0.3rem',
                    borderRadius: '4px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-text-muted)'}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Group Form */}
      {showCreateGroup && (
        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          background: 'rgb(var(--rgb-ink) / 0.03)',
          border: '1px solid rgb(var(--rgb-ink) / 0.1)',
          borderRadius: '8px'
        }}>
          <form onSubmit={handleCreateGroup} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Nom du groupe"
              style={{
                flex: 1,
                background: 'rgb(var(--rgb-ink) / 0.05)',
                border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                borderRadius: '6px',
                padding: '0.6rem 0.8rem',
                color: 'var(--color-text)',
                fontSize: '0.875rem'
              }}
            />
            <input
              type="color"
              value={newGroupColor}
              onChange={(e) => setNewGroupColor(e.target.value)}
              style={{
                width: '40px',
                height: '36px',
                border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                borderRadius: '6px',
                cursor: 'pointer',
                background: 'transparent'
              }}
            />
            <button
              type="submit"
              disabled={createGroupMutation.isLoading}
              style={{
                background: 'rgb(var(--rgb-brand) / 0.15)',
                border: '1px solid rgb(var(--rgb-brand) / 0.35)',
                color: 'rgb(var(--rgb-brand))',
                padding: '0.6rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Créer
            </button>
            <button
              type="button"
              onClick={() => setShowCreateGroup(false)}
              style={{
                background: 'transparent',
                border: '1px solid rgb(var(--rgb-ink) / 0.15)',
                color: 'var(--color-text-secondary)',
                padding: '0.6rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Annuler
            </button>
          </form>
        </div>
      )}

      {/* Recipients/Members List */}
      {recipients.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: 'rgb(var(--rgb-ink) / 0.02)',
          borderRadius: '12px',
          border: '1px solid rgb(var(--rgb-ink) / 0.1)'
        }}>
          <Users size={48} style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }} />
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem' }}>
            {searchQuery ? 'Aucun destinataire trouvé' : (currentGroup ? 'Aucun membre dans ce groupe' : 'Aucun destinataire')}
          </p>
        </div>
      ) : (
        <div style={{
          background: 'rgb(var(--rgb-ink) / 0.02)',
          borderRadius: '12px',
          border: '1px solid rgb(var(--rgb-ink) / 0.1)',
          overflow: 'hidden'
        }}>
          {recipients.map((recipient, index) => (
            <div
              key={recipient.id}
              style={{
                padding: '0.9rem 1.2rem',
                display: 'grid',
                gridTemplateColumns: '1.5fr 1fr auto',
                gap: '1rem',
                alignItems: 'center',
                borderBottom: index < recipients.length - 1 ? '1px solid rgb(var(--rgb-ink) / 0.05)' : 'none'
              }}
            >
              <div>
                <div style={{ fontWeight: 600, color: 'var(--color-text)', marginBottom: '0.2rem' }}>
                  {recipient.fullName || recipient.email}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                  <Mail size={12} />
                  {recipient.email}
                </div>
                {!currentGroup && recipient.groupNames && recipient.groupNames.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                    {recipient.groupNames.map((group) => (
                      <span
                        key={group.id}
                        onClick={() => enterGroup(group)}
                        style={{
                          display: 'inline-block',
                          padding: '0.1rem 0.4rem',
                          background: `${group.color}15`,
                          border: `1px solid ${group.color}40`,
                          color: group.color,
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                      >
                        {group.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                {recipient.createdAt ? new Date(recipient.createdAt).toLocaleDateString() : '-'}
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                {!currentGroup && (
                  <button
                    onClick={() => setEnrollRecipient(recipient)}
                    style={{
                      background: 'rgb(var(--rgb-brand) / 0.1)',
                      border: '1px solid rgb(var(--rgb-brand) / 0.3)',
                      color: 'rgb(var(--rgb-brand))',
                      padding: '0.35rem 0.6rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.75rem'
                    }}
                  >
                    Inscrire
                  </button>
                )}
                {currentGroup ? (
                  <button
                    onClick={() => removeFromGroupMutation.mutate({ groupId: currentGroup.id, recipientId: recipient.id })}
                    style={{
                      background: 'rgba(239,68,68,0.1)',
                      border: '1px solid rgba(239,68,68,0.3)',
                      color: '#ef4444',
                      padding: '0.35rem 0.6rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem'
                    }}
                  >
                    <UserMinus size={12} />
                    Retirer
                  </button>
                ) : (
                  <button
                    onClick={() => deleteMutation.mutate(recipient.id)}
                    style={{
                      background: 'rgba(239,68,68,0.1)',
                      border: '1px solid rgba(239,68,68,0.3)',
                      color: '#ef4444',
                      padding: '0.35rem 0.6rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem'
                    }}
                  >
                    <Trash2 size={12} />
                    Supprimer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Numbered Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      {enrollRecipient && (
        <EnrollRecipientModal
          recipient={enrollRecipient}
          sequences={sequences}
          onClose={() => setEnrollRecipient(null)}
          onEnroll={async ({ sequenceId, forceReset }) => {
            await enrollRecipientInSequence(sequenceId, {
              email: enrollRecipient.email,
              firstName: enrollRecipient.firstName,
              lastName: enrollRecipient.lastName,
              forceReset
            })
            queryClient.invalidateQueries({ queryKey: ['sequenceEnrollments'] })
          }}
        />
      )}

      {showAddMembersModal && currentGroup && (
        <AddMembersToGroupModal
          group={currentGroup}
          onClose={() => setShowAddMembersModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['recipientGroupMembers'] })
            queryClient.invalidateQueries({ queryKey: ['recipientGroups'] })
          }}
        />
      )}
    </div>
  )
}

function EnrollRecipientModal({ recipient, sequences, onClose, onEnroll }) {
  const [sequenceId, setSequenceId] = useState('')
  const [forceReset, setForceReset] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleEnroll = async () => {
    if (!sequenceId) {
      toast.error('Séquence requise')
      return
    }
    setSubmitting(true)
    try {
      await onEnroll({ sequenceId, forceReset })
      toast.success('Destinataire inscrit')
      onClose()
    } catch (error) {
      toast.error(error?.response?.data?.error || error?.message || 'Erreur lors de l\'inscription')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgb(var(--rgb-shade) / 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'rgb(var(--rgb-surface-hover))',
        borderRadius: '14px',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '460px',
        border: '1px solid rgb(var(--rgb-ink) / 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Inscrire un destinataire</h3>
            <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>{recipient.email}</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.4rem', color: 'var(--color-text-secondary)', fontSize: '0.8rem' }}>Séquence</label>
          <select
            value={sequenceId}
            onChange={(e) => setSequenceId(e.target.value)}
            style={{
              width: '100%',
              padding: '0.7rem',
              background: 'rgb(var(--rgb-ink) / 0.05)',
              border: '1px solid rgb(var(--rgb-ink) / 0.1)',
              borderRadius: '8px',
              color: 'var(--color-text)'
            }}
          >
            <option value="">Sélectionner...</option>
            {sequences.map((sequence) => (
              <option key={sequence.id} value={sequence.id}>
                {sequence.name}
              </option>
            ))}
          </select>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'rgb(var(--rgb-ink) / 0.75)', fontSize: '0.85rem' }}>
          <input type="checkbox" checked={forceReset} onChange={(e) => setForceReset(e.target.checked)} />
          Réinitialiser si déjà inscrit
        </label>

        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: '1px solid rgb(var(--rgb-ink) / 0.15)',
              color: 'rgb(var(--rgb-ink) / 0.75)',
              padding: '0.6rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleEnroll}
            disabled={submitting}
            style={{
              background: 'rgb(var(--rgb-brand) / 0.15)',
              border: '1px solid rgb(var(--rgb-brand) / 0.35)',
              color: 'rgb(var(--rgb-brand))',
              padding: '0.6rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Inscrire
          </button>
        </div>
      </div>
    </div>
  )
}

// Modal for adding members to a group
function AddMembersToGroupModal({ group, onClose, onSuccess }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const limit = 50

  const { data, isLoading } = useQuery({
    queryKey: ['recipients', searchQuery, page, limit],
    queryFn: () => getRecipients({ q: searchQuery || null, page, limit }),
    keepPreviousData: true
  })

  const recipients = data?.items || []
  const total = data?.total || 0
  const totalPages = Math.ceil(total / limit)

  const mutation = useMutation({
    mutationFn: () => addRecipientsToGroup(group.id, Array.from(selectedIds)),
    onSuccess: (data) => {
      toast.success(`${data.added || selectedIds.size} membre(s) ajouté(s) au groupe`)
      onSuccess?.()
      onClose()
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || err.message || 'Erreur lors de l\'ajout')
    }
  })

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === recipients.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(recipients.map(r => r.id)))
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (selectedIds.size === 0) {
      toast.error('Aucun destinataire sélectionné')
      return
    }
    mutation.mutate()
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgb(var(--rgb-shade) / 0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        background: 'rgb(var(--rgb-surface-hover))',
        borderRadius: '14px',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '600px',
        border: '1px solid rgb(var(--rgb-ink) / 0.1)',
        maxHeight: '80vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem'
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600 }}>
                Ajouter des membres
              </h3>
              <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                Groupe : <span style={{ color: group.color }}>{group.name}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
                padding: '0.25rem'
              }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Search */}
          <div style={{ position: 'relative', marginBottom: '1rem' }}>
            <Search size={16} style={{
              position: 'absolute',
              left: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-text-muted)'
            }} />
            <input
              type="text"
              placeholder="Rechercher des destinataires..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setPage(1)
              }}
              style={{
                width: '100%',
                background: 'rgb(var(--rgb-ink) / 0.05)',
                border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                borderRadius: '8px',
                padding: '0.6rem 0.75rem 0.6rem 2.25rem',
                color: 'var(--color-text)',
                fontSize: '0.9rem'
              }}
            />
          </div>

          {/* Select all */}
          {recipients.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.75rem',
              padding: '0.5rem 0.75rem',
              background: 'rgb(var(--rgb-ink) / 0.03)',
              borderRadius: '6px'
            }}>
              <button
                type="button"
                onClick={toggleSelectAll}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                {selectedIds.size === recipients.length && recipients.length > 0 ? (
                  <CheckSquare size={18} style={{ color: '#3b82f6' }} />
                ) : selectedIds.size > 0 ? (
                  <CheckSquare size={18} style={{ color: 'var(--color-text-muted)' }} />
                ) : (
                  <Square size={18} style={{ color: 'var(--color-text-muted)' }} />
                )}
              </button>
              <span style={{ fontSize: '0.85rem', color: 'rgb(var(--rgb-ink) / 0.75)' }}>
                {selectedIds.size > 0 ? `${selectedIds.size} sélectionné(s)` : 'Tout sélectionner'}
              </span>
            </div>
          )}

          {/* Recipients list */}
          <div style={{ flex: 1, overflowY: 'auto', marginBottom: '1rem' }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : recipients.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                background: 'rgb(var(--rgb-ink) / 0.02)',
                borderRadius: '8px',
                color: 'var(--color-text-secondary)'
              }}>
                <Users size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
                <p>{searchQuery ? 'Aucun destinataire trouvé' : 'Aucun destinataire disponible'}</p>
              </div>
            ) : (
              <div style={{ maxHeight: '300px' }}>
                {recipients.map((recipient) => {
                  const isSelected = selectedIds.has(recipient.id)
                  const isAlreadyInGroup = recipient.groupNames?.some(g => g.id === group.id)
                  
                  return (
                    <div
                      key={recipient.id}
                      onClick={() => !isAlreadyInGroup && toggleSelect(recipient.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.6rem 0.75rem',
                        marginBottom: '0.35rem',
                        background: isSelected ? 'rgba(59,130,246,0.1)' : isAlreadyInGroup ? 'rgb(var(--rgb-ink) / 0.01)' : 'rgb(var(--rgb-ink) / 0.02)',
                        border: isSelected ? '1px solid rgba(59,130,246,0.3)' : '1px solid rgb(var(--rgb-ink) / 0.08)',
                        borderRadius: '8px',
                        cursor: isAlreadyInGroup ? 'not-allowed' : 'pointer',
                        opacity: isAlreadyInGroup ? 0.5 : 1,
                        transition: 'all 0.15s'
                      }}
                    >
                      {isAlreadyInGroup ? (
                        <CheckSquare size={18} style={{ color: '#10b981', flexShrink: 0 }} />
                      ) : isSelected ? (
                        <CheckSquare size={18} style={{ color: '#3b82f6', flexShrink: 0 }} />
                      ) : (
                        <Square size={18} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, fontSize: '0.9rem', color: 'var(--color-text)' }}>
                          {recipient.fullName || recipient.email}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                          {recipient.email}
                        </div>
                      </div>
                      {isAlreadyInGroup && (
                        <span style={{
                          fontSize: '0.7rem',
                          padding: '0.15rem 0.4rem',
                          background: 'rgba(16,185,129,0.15)',
                          color: '#10b981',
                          borderRadius: '4px'
                        }}>
                          Déjà membre
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{
                  padding: '0.4rem 0.75rem',
                  background: page > 1 ? 'rgb(var(--rgb-ink) / 0.1)' : 'transparent',
                  border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                  borderRadius: '6px',
                  color: page > 1 ? 'rgb(var(--rgb-ink) / 0.75)' : 'var(--color-text-disabled)',
                  cursor: page > 1 ? 'pointer' : 'not-allowed',
                  fontSize: '0.8rem'
                }}
              >
                <ChevronLeft size={14} />
              </button>
              <span style={{ padding: '0.4rem 0.75rem', color: 'var(--color-text-secondary)', fontSize: '0.8rem' }}>
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{
                  padding: '0.4rem 0.75rem',
                  background: page < totalPages ? 'rgb(var(--rgb-ink) / 0.1)' : 'transparent',
                  border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                  borderRadius: '6px',
                  color: page < totalPages ? 'rgb(var(--rgb-ink) / 0.75)' : 'var(--color-text-disabled)',
                  cursor: page < totalPages ? 'pointer' : 'not-allowed',
                  fontSize: '0.8rem'
                }}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: '1px solid rgb(var(--rgb-ink) / 0.15)',
                color: 'rgb(var(--rgb-ink) / 0.75)',
                padding: '0.65rem 1.25rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || selectedIds.size === 0}
              style={{
                background: selectedIds.size === 0 ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.15)',
                border: `1px solid ${selectedIds.size === 0 ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.35)'}`,
                color: selectedIds.size === 0 ? 'var(--color-text-muted)' : '#3b82f6',
                padding: '0.65rem 1.25rem',
                borderRadius: '8px',
                cursor: selectedIds.size === 0 || mutation.isPending ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {mutation.isPending ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Ajout...
                </>
              ) : (
                <>
                  <UserPlus size={16} />
                  Ajouter {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
